import { __assign, __awaiter, __generator, __values } from "tslib";
export { isError } from "./error";
import { toHashMap } from "./hashmap";
import { VERSION } from "./version";
import { PushApi } from "./api";
import { Environment } from "./environment";
import { Span, toBacktraceMatcher } from "./span";
export { Span };
import { Queue } from "./queue";
import { Dispatcher } from "./dispatcher";
var Appsignal = (function () {
    function Appsignal(options) {
        var e_1, _a;
        this.VERSION = VERSION;
        this.ignored = [];
        this.backtraceMatchers = [];
        this._breadcrumbs = [];
        this._hooks = {
            decorators: Array(),
            overrides: Array()
        };
        this._env = Environment.serialize();
        this._queue = new Queue([]);
        var _b = options.key, key = _b === void 0 ? "" : _b, uri = options.uri, revision = options.revision, ignoreErrors = options.ignoreErrors, matchBacktracePaths = options.matchBacktracePaths;
        if (revision && typeof revision !== "string") {
            options.revision = String(revision);
        }
        if (key === "") {
            console.info("[APPSIGNAL]: No API key provided. Started in development mode. No data will be sent.");
        }
        this._api = new PushApi({
            key: key,
            uri: uri,
            version: this.VERSION
        });
        if (ignoreErrors && Array.isArray(ignoreErrors)) {
            this.ignored = ignoreErrors
                .filter(function (value) { return value instanceof RegExp; })
                .map(unglobalize);
        }
        if (matchBacktracePaths) {
            var paths = void 0;
            if (Array.isArray(matchBacktracePaths)) {
                paths = matchBacktracePaths;
            }
            else {
                paths = [matchBacktracePaths];
            }
            try {
                for (var paths_1 = __values(paths), paths_1_1 = paths_1.next(); !paths_1_1.done; paths_1_1 = paths_1.next()) {
                    var matcher = paths_1_1.value;
                    if (matcher instanceof RegExp) {
                        this.backtraceMatchers.push(toBacktraceMatcher(unglobalize(matcher)));
                    }
                    else if (typeof matcher === "function") {
                        this.backtraceMatchers.push(matcher);
                    }
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (paths_1_1 && !paths_1_1.done && (_a = paths_1.return)) _a.call(paths_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        this._dispatcher = new Dispatcher(this._queue, this._api);
        this._options = options;
    }
    Appsignal.prototype.send = function (data, tagsOrFn, namespace) {
        var e_2, _a, e_3, _b;
        var _this = this;
        var _c;
        if (!(data instanceof Error) &&
            !(data instanceof Span) &&
            !(data && data.error instanceof Error)) {
            console.error("[APPSIGNAL]: Can't send error, given error is not a valid type");
            return;
        }
        var error;
        if ("error" in data) {
            error = data.error;
        }
        else {
            error = data;
        }
        var span = error instanceof Span ? error : this._createSpanFromError(error);
        try {
            for (var _d = __values(this._hooks.decorators), _e = _d.next(); !_e.done; _e = _d.next()) {
                var decorator = _e.value;
                var previousSpan = span;
                span = decorator(span);
                if (span === undefined) {
                    span = previousSpan;
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
            }
            finally { if (e_2) throw e_2.error; }
        }
        if (tagsOrFn) {
            if (typeof tagsOrFn === "function") {
                var callback = tagsOrFn;
                callback(span);
            }
            else {
                console.warn("[APPSIGNAL]: DEPRECATED: Calling the `send`/`sendError` function with a tags object is deprecated. Use the callback argument instead.");
                var tags = (toHashMap(tagsOrFn) || {});
                span.setTags(tags);
            }
        }
        if (namespace) {
            console.warn("[APPSIGNAL]: DEPRECATED: Calling the `send`/`sendError` function with a namespace is deprecated. Use the callback argument instead.");
            span.setNamespace(namespace);
        }
        if (this._breadcrumbs.length > 0)
            span.setBreadcrumbs(this._breadcrumbs);
        try {
            for (var _f = __values(this._hooks.overrides), _g = _f.next(); !_g.done; _g = _f.next()) {
                var override = _g.value;
                var previousSpan = span;
                var nextSpan = override(span);
                if (nextSpan === false) {
                    console.warn("[APPSIGNAL]: Ignored a span due to override.");
                    return;
                }
                span = nextSpan !== null && nextSpan !== void 0 ? nextSpan : previousSpan;
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_b = _f.return)) _b.call(_f);
            }
            finally { if (e_3) throw e_3.error; }
        }
        var message = (_c = span.getError()) === null || _c === void 0 ? void 0 : _c.message;
        if (message && this.ignored.some(function (el) { return el.test(message); })) {
            console.warn("[APPSIGNAL]: Ignored a span: ".concat(message));
            return;
        }
        span.cleanBacktracePath(this.backtraceMatchers);
        if (Environment.supportsPromises()) {
            this._breadcrumbs = [];
            if (!this._options.key) {
                console.warn("[APPSIGNAL]: Span not sent because we're in development mode:", span);
            }
            else {
                return this._api.push(span).catch(function () {
                    _this._queue.push(span);
                    setTimeout(function () { return _this._dispatcher.schedule(); }, 0);
                });
            }
        }
        else {
            console.error("[APPSIGNAL]: Error not sent. A Promise polyfill is required.");
            return;
        }
    };
    Appsignal.prototype.sendError = function (error, tagsOrFn, namespace) {
        return this.send(error, tagsOrFn, namespace);
    };
    Appsignal.prototype.use = function (plugin) {
        plugin.call(this);
    };
    Appsignal.prototype.createSpan = function (fn) {
        var _a = this._options, _b = _a.revision, revision = _b === void 0 ? "" : _b, namespace = _a.namespace;
        var span = new Span({
            environment: this._env,
            revision: revision
        });
        if (namespace)
            span.setNamespace(namespace);
        if (fn && typeof fn === "function")
            fn(span);
        return span;
    };
    Appsignal.prototype.wrap = function (fn, tagsOrFn, namespace) {
        return __awaiter(this, void 0, void 0, function () {
            var e_4;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 5]);
                        return [4, fn()];
                    case 1: return [2, _a.sent()];
                    case 2:
                        e_4 = _a.sent();
                        if (!(e_4 instanceof Error || e_4 instanceof ErrorEvent)) return [3, 4];
                        return [4, this.sendError(e_4, tagsOrFn, namespace)];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4: return [2, Promise.reject(e_4)];
                    case 5: return [2];
                }
            });
        });
    };
    Appsignal.prototype.addDecorator = function (decorator) {
        this._hooks.decorators.push(decorator);
    };
    Appsignal.prototype.addOverride = function (override) {
        this._hooks.overrides.push(override);
    };
    Appsignal.prototype.demo = function () {
        var span = this._createSpanFromError(new Error("Hello world! This is an error used for demonstration purposes."));
        span
            .setAction("TestAction")
            .setParams({
            path: "/hello",
            method: "GET"
        })
            .setTags({
            demo_sample: "true"
        });
        this.send(span);
    };
    Appsignal.prototype.addBreadcrumb = function (breadcrumb) {
        var crumb = __assign(__assign({ timestamp: Math.round(new Date().getTime() / 1000) }, breadcrumb), { metadata: toHashMap(breadcrumb.metadata) });
        if (!crumb.category) {
            console.warn("[APPSIGNAL]: Breadcrumb not added. `category` is missing.");
            return;
        }
        if (!crumb.action) {
            console.warn("[APPSIGNAL]: Breadcrumb not added. `action` is missing.");
            return;
        }
        if (this._breadcrumbs.length === 20) {
            this._breadcrumbs.pop();
        }
        this._breadcrumbs.unshift(crumb);
    };
    Appsignal.prototype._createSpanFromError = function (error) {
        var event = this.createSpan();
        event.setError(error);
        return event;
    };
    return Appsignal;
}());
export default Appsignal;
function unglobalize(regexp) {
    return new RegExp(regexp.source, regexp.flags.replace("g", ""));
}
//# sourceMappingURL=index.js.map